/**
 * src/lib/nav/searchOpenRouter.js
 *
 * Pure router for the `acn:search-open` event SmartSearchOverlay dispatches when
 * a global-search result is clicked. Maps { type, id } to the right tab plus a
 * race-free deep-link request (projectNav / dealNav) so the target view opens
 * the specific entity even when it was not mounted at dispatch time (the known
 * "lands on the list, not the detail; second click works" race).
 *
 * Extracted from AppShell so the routing is unit-testable. The `deal` branch
 * previously only switched to the Pipeline tab and never called
 * requestDealOpen(id) -> PipelineView received no open-request and the deal
 * detail drawer never opened, so clicking a deal search result was a silent
 * no-op (CEO 2026-06-21: "click does not load the details pager").
 */
import { requestProjectOpen } from './projectNav';
import { requestDealOpen } from './dealNav';
import { requestContactOpen } from './contactNav';
import { requestVesselOpen } from './vesselNav';

/**
 * @param {{type?: string, id?: string}|undefined} detail - the CustomEvent detail
 * @param {(tab: string, reason: string) => void} setActiveTab - tab switcher
 */
export function routeSearchOpen(detail, setActiveTab) {
  const { type, id } = detail || {};
  if (type === 'task') setActiveTab('focus', 'event:search-open');
  else if (type === 'project' || type === 'bundle') {
    if (type === 'project' && id) requestProjectOpen(id); // race-free deep-link into the detail
    setActiveTab('projects', 'event:search-open');
  }
  else if (type === 'person' || type === 'contact') {
    if (id) requestContactOpen(id); // race-free deep-link into the directory contact detail
    setActiveTab('directory', 'event:search-open');
  }
  else if (type === 'deal') {
    if (id) requestDealOpen(id); // race-free deep-link into the pipeline deal drawer (was missing)
    setActiveTab('pipeline', 'event:search-open');
  }
  else if (type === 'vessel') {
    if (id) requestVesselOpen(id); // race-free deep-link into the directory vessel detail
    setActiveTab('directory', 'event:search-open');
  }
}
